import { useEffect, useState, type JSX } from 'react';
import { Link } from 'react-router-dom';
import Enquiry from './Enquiry';
import { formatDimensions } from '../lib/formatDimensions';
import type { Painting } from '../types/painting';

interface LightboxProps {
  painting: Painting;
  paintings: Painting[];
  onClose: () => void;
}

function getAdjacentId(paintings: Painting[], currentId: string, offset: number): string | null {
  const index = paintings.findIndex((p) => p.id === currentId);
  if (index === -1 || paintings.length === 0) return null;
  const nextIndex = (index + offset + paintings.length) % paintings.length;
  return paintings[nextIndex]?.id ?? null;
}

export default function Lightbox({ painting, paintings, onClose }: LightboxProps): JSX.Element {
  const [currentId, setCurrentId] = useState(painting.id);
  const [visible, setVisible] = useState(false);
  const [enquiring, setEnquiring] = useState(false);

  const current = paintings.find((p) => p.id === currentId) ?? painting;
  const isSold = current.status === 'sold';

  useEffect(() => {
    setVisible(true);
  }, []);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return (): void => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    function handleKeydown(event: KeyboardEvent): void {
      // Let the enquiry form's own fields (and its Escape-to-close) work
      // without the lightbox stealing arrow keys or closing behind it.
      if (enquiring) return;
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key === 'ArrowLeft') {
        const nextId = getAdjacentId(paintings, currentId, -1);
        if (nextId !== null) setCurrentId(nextId);
      }
      if (event.key === 'ArrowRight') {
        const nextId = getAdjacentId(paintings, currentId, 1);
        if (nextId !== null) setCurrentId(nextId);
      }
    }
    window.addEventListener('keydown', handleKeydown);
    return (): void => {
      window.removeEventListener('keydown', handleKeydown);
    };
  }, [paintings, currentId, onClose, enquiring]);

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 transition-opacity duration-200 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
      >
        <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>

      <button
        type="button"
        aria-label="Previous painting"
        onClick={() => {
          const nextId = getAdjacentId(paintings, currentId, -1);
          if (nextId !== null) setCurrentId(nextId);
        }}
        className="absolute left-2 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 md:left-6"
      >
        <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 6l-6 6 6 6" />
        </svg>
      </button>

      <button
        type="button"
        aria-label="Next painting"
        onClick={() => {
          const nextId = getAdjacentId(paintings, currentId, 1);
          if (nextId !== null) setCurrentId(nextId);
        }}
        className="absolute right-2 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 md:right-6"
      >
        <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 6l6 6-6 6" />
        </svg>
      </button>

      <div className="flex max-h-full max-w-3xl flex-col items-center gap-4 overflow-y-auto">
        <img src={current.imageUrl} alt={current.title} className="max-h-[70vh] max-w-full rounded-md object-contain" />

        <div className="text-center text-white">
          <h2 className="text-xl font-semibold">{current.title}</h2>
          <p className="text-sm text-neutral-300">
            {current.style.join(', ')} · {formatDimensions(current.height, current.width)}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            disabled={isSold}
            onClick={() => {
              setEnquiring(true);
            }}
            className="rounded bg-blue-600 px-5 py-2 text-white disabled:cursor-not-allowed disabled:bg-neutral-500"
          >
            {isSold ? 'Sold' : 'Enquire'}
          </button>
          <Link to="/contactMe" className="text-sm text-neutral-300 underline hover:text-white">
            Reach out to me
          </Link>
        </div>
      </div>

      {enquiring ? (
        <Enquiry
          painting={current}
          onClose={() => {
            setEnquiring(false);
          }}
        />
      ) : null}
    </div>
  );
}
